import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, BRAND_LOGO, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
      nav(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md bg-white border-2 border-[#121212] p-8">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <img src={BRAND_LOGO} alt="" className="h-12 w-12 object-contain" />
          <div className="leading-none">
            <div className="font-display font-extrabold text-sm">AUTO PARTS</div>
            <div className="font-display font-extrabold text-sm text-[#EF3340]">AFRICA</div>
          </div>
        </Link>
        <h1 className="font-display text-3xl font-extrabold mb-2">Welcome back</h1>
        <p className="text-sm text-zinc-600 mb-6">Sign in to track orders, manage parts, or verify listings.</p>

        <button onClick={googleLogin} className="w-full border-2 border-[#121212] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#121212] hover:text-white transition-colors flex items-center justify-center gap-3 mb-5" data-testid="login-google">
          <GoogleIcon /> Continue with Google
        </button>
        <div className="flex items-center gap-3 mb-5 text-xs text-zinc-400 uppercase tracking-wider">
          <div className="flex-1 h-px bg-[#E4E4E7]" />
          <span>or</span>
          <div className="flex-1 h-px bg-[#E4E4E7]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-apa" data-testid="login-email" />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-apa" data-testid="login-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full !py-3.5" data-testid="login-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Log in
          </button>
        </form>
        <p className="text-sm text-zinc-600 mt-6 text-center">
          New here?{" "}
          <Link to="/register" className="font-bold text-[#009A44] hover:underline">Create an account</Link>
        </p>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.3 29 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.4 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.3 29 3 24 3 16.3 3 9.6 7.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c4.9 0 9.4-1.9 12.8-5l-5.9-5c-2 1.4-4.4 2-6.9 2-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 40.6 16.3 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l5.9 5c2.4-2.3 4-5.4 4.6-8.9.3-1.2.4-2.3.4-3.5 0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
